import { Icon, Portal, useDisclosure } from '@chakra-ui/react';
import {
  IAccountRespSchema,
  IOfferingRes,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRespSchema,
  IRateResSchema,
  OnQuoteOfferingChange,
  PriceDisplayEnum,
} from '@monetize/types/app';
import { useRef, useState } from 'react';
import { MdClose, MdSettings } from 'react-icons/md';
import { useConfirmModal } from '../../services/confirmModal';
import { useFlags } from '../../services/launchDarkly';
import {
  MButton,
  MDrawer,
  MDrawerBody,
  MDrawerCloseButton,
  MDrawerContent,
  MDrawerFooter,
  MDrawerHeader,
  MDrawerOverlay,
  MDrawerPositioner,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
  MVStack,
} from '../Monetize';
import { PriceDisplayFrequencyConfiguration } from './PriceDisplayFrequencyConfiguration';
import { PriceDisplaySettingsConfiguration } from './PriceDisplaySettingsConfiguration';
import { PriceUpliftCalculator } from './PriceUpliftCalculator';
import { PriceUpliftConfiguration } from './PriceUpliftConfiguration';

type QuoteOfferingConfigurationDrawerProps = {
  account: IAccountRespSchema | Pick<IAccountRespSchema, 'id' | 'accountName'>;
  isReadOnly: boolean;
  isDisabled: boolean;
  quoteOffering?: IQuoteOfferingRespSchema | null;
  childQuoteOfferings?: IQuoteOfferingRespSchema[];
  offering: IOfferingRes | null;
  offeringRate: IRateResSchema | null;
  quote?: IQuoteRespSchema | null;
  isOfferingLoading: boolean;
  quotePrices: IQuotePrice[];
  onOfferingChange: OnQuoteOfferingChange;
  renderTrigger?: ({
    onOpen,
    isDisabled,
  }: {
    onOpen: () => void;
    isDisabled: boolean;
  }) => React.ReactElement;
  handleQuoteOfferingUpdate: ({
    priceDisplay,
  }: {
    priceDisplay?: PriceDisplayEnum;
  }) => void;
  updateQuoteOffering: (
    id: string,
    data: IQuoteOfferingReqSchema,
  ) => Promise<IQuoteOfferingRespSchema | null>;
};

const TABS = [
  {
    id: 'price-uplift',
    title: 'Price Uplift',
  },
  {
    id: 'display-settings',
    title: 'Display Settings',
  },
];
export const QuoteOfferingConfigurationDrawer = ({
  account,
  isReadOnly,
  isDisabled,
  quoteOffering,
  childQuoteOfferings = [],
  offering,
  offeringRate,
  quote,
  isOfferingLoading,
  quotePrices,
  onOfferingChange,
  renderTrigger,
  handleQuoteOfferingUpdate,
  updateQuoteOffering,
}: QuoteOfferingConfigurationDrawerProps) => {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();
  const flags = useFlags();
  const { enableUpliftCalculator } = flags;
  const [editMode, setEditMode] = useState(false);

  const handleClose = () => {
    if (editMode) {
      showConfirmModal({
        title: 'Unsaved Changes',
        description:
          'You have unsaved changes. Are you sure you want to close?',
        onYes: () => {
          setEditMode(false);
          onClose();
          closeConfirmationModal();
        },
        yesBtnProps: {
          variant: 'primary',
        },
        noBtnProps: {
          variant: 'cancel',
        },
      });
    } else {
      onClose();
      closeConfirmationModal();
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ onOpen, isDisabled })
      ) : (
        <MButton
          leftIcon={<MdSettings />}
          ref={btnRef}
          variant="tertiary"
          size="sm"
          onClick={onOpen}
          isDisabled={isDisabled}
          data-testid="price-uplift-configure-btn"
        >
          Configure
        </MButton>
      )}
      {quote && quoteOffering && offeringRate && isOpen && (
        <MDrawer
          size="lg"
          placement="end"
          open={isOpen}
          onOpenChange={(e) => {
            if (!e.open) {
              handleClose();
            }
          }}
          finalFocusEl={() => btnRef.current}
          preventScroll={false}
        >
          <Portal>
            <MDrawerOverlay />
            <MDrawerPositioner>
              <MDrawerContent>
                <MDrawerCloseButton>
                  <Icon asChild>
                    <MdClose />
                  </Icon>
                </MDrawerCloseButton>
                <MDrawerHeader
                  fontSize="md"
                  borderBottomColor="tGray.back"
                  borderBottomWidth="1px"
                  borderBottomStyle="solid"
                >
                  {quoteOffering?.offeringName}
                </MDrawerHeader>
                <MDrawerBody
                  p={0}
                  bg="tGray.sidebarDark"
                  color="tGray.darkPurple"
                >
                  <MTabs variant="line" size="sm" bg="tWhite.base">
                    <MTabList px={6} py={2}>
                      {TABS.map(({ id, title }) => (
                        <MTab key={id}>{title}</MTab>
                      ))}
                    </MTabList>
                    <MTabPanels p={2} bg="tGray.sidebarDark">
                      {enableUpliftCalculator ? (
                        <MTabPanel p={2} bg="tGray.sidebarDark">
                          <PriceUpliftCalculator
                            account={account}
                            isReadOnly={isReadOnly}
                            quote={quote}
                            quoteOffering={quoteOffering}
                            childQuoteOfferings={childQuoteOfferings}
                            offeringRate={offeringRate}
                            onOfferingChange={onOfferingChange}
                            quotePrices={quotePrices}
                            updateQuoteOffering={updateQuoteOffering}
                            setEditMode={setEditMode}
                            closeDrawer={onClose}
                          />
                        </MTabPanel>
                      ) : (
                        <MTabPanel p={4} bg="tWhite.base" borderRadius={2}>
                          <PriceUpliftConfiguration
                            isReadOnly={isReadOnly}
                            quoteOffering={quoteOffering}
                            onOfferingChange={onOfferingChange}
                          />
                        </MTabPanel>
                      )}
                      <MTabPanel p={0}>
                        <MVStack gap="4">
                          <PriceDisplaySettingsConfiguration
                            isReadOnly={isReadOnly}
                            quoteOffering={quoteOffering}
                            handleQuoteOfferingUpdate={
                              handleQuoteOfferingUpdate
                            }
                            offering={offering}
                            offeringRate={offeringRate}
                            quote={quote}
                            isOfferingLoading={isOfferingLoading}
                          />
                          <PriceDisplayFrequencyConfiguration
                            isReadOnly={isReadOnly}
                            quoteOffering={quoteOffering}
                            onOfferingChange={onOfferingChange}
                            offering={offering}
                            quote={quote}
                            isOfferingLoading={isOfferingLoading}
                          />
                        </MVStack>
                      </MTabPanel>
                    </MTabPanels>
                  </MTabs>
                </MDrawerBody>
                <MDrawerFooter
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MButton type="button" variant="cancel" onClick={handleClose}>
                    Close
                  </MButton>
                </MDrawerFooter>
              </MDrawerContent>
            </MDrawerPositioner>
          </Portal>
        </MDrawer>
      )}
    </>
  );
};
